import { Suspense, lazy } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { GuestRoute } from "./GuestRoute";
import { PlatformAdminRoute } from "./PlatformAdminRoute";
import { RootGate } from "./RootGate";
import { SetupGate } from "./SetupGate";
import { AppLayout } from "../components/AppLayout";
import { FeatureGate } from "../components/FeatureGate";
import { LoadingSpinner } from "../components/LoadingSpinner";
// Eager: the guest-facing surface. The landing page (imported by RootGate) and
// the auth pages are what a first-time visitor renders, so they live in the
// entry chunk — no spinner between landing and login/register.
import { LoginPage } from "../pages/LoginPage";
import { RegisterPage } from "../pages/RegisterPage";
import { ForgotPasswordPage } from "../pages/ForgotPasswordPage";
import { ResetPasswordPage } from "../pages/ResetPasswordPage";
import { VerifyEmailPage } from "../pages/VerifyEmailPage";
import { JoinBusinessPage } from "../pages/JoinBusinessPage";
import { NotFoundPage } from "../pages/NotFoundPage";

// Route-level code splitting (ADR 0013): everything behind authentication —
// plus the rarely-visited legal pages — loads on demand. The first visit to a
// page shows a brief Suspense fallback while its chunk downloads; after that
// it's cached. This keeps the entry bundle (what a guest must download before
// the landing paints) small.
const HomePage = lazy(() => import("../pages/HomePage").then((m) => ({ default: m.HomePage })));
const CustomersPage = lazy(() => import("../pages/CustomersPage").then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() => import("../pages/CustomerDetailPage").then((m) => ({ default: m.CustomerDetailPage })));
const SuppliersPage = lazy(() => import("../pages/SuppliersPage").then((m) => ({ default: m.SuppliersPage })));
const SupplierDetailPage = lazy(() => import("../pages/SupplierDetailPage").then((m) => ({ default: m.SupplierDetailPage })));
const SalesPage = lazy(() => import("../pages/SalesPage").then((m) => ({ default: m.SalesPage })));
const SaleDetailPage = lazy(() => import("../pages/SaleDetailPage").then((m) => ({ default: m.SaleDetailPage })));
const ExpensesPage = lazy(() => import("../pages/ExpensesPage").then((m) => ({ default: m.ExpensesPage })));
const ExpenseDetailPage = lazy(() => import("../pages/ExpenseDetailPage").then((m) => ({ default: m.ExpenseDetailPage })));
const CatalogPage = lazy(() => import("../pages/CatalogPage").then((m) => ({ default: m.CatalogPage })));
const CatalogDetailPage = lazy(() => import("../pages/CatalogDetailPage").then((m) => ({ default: m.CatalogDetailPage })));
const InventoryPage = lazy(() => import("../pages/InventoryPage").then((m) => ({ default: m.InventoryPage })));
const InventoryDetailPage = lazy(() => import("../pages/InventoryDetailPage").then((m) => ({ default: m.InventoryDetailPage })));
const EmployeesPage = lazy(() => import("../pages/EmployeesPage").then((m) => ({ default: m.EmployeesPage })));
const BusinessSettingsPage = lazy(() => import("../pages/BusinessSettingsPage").then((m) => ({ default: m.BusinessSettingsPage })));
const AccountSettingsPage = lazy(() => import("../pages/AccountSettingsPage").then((m) => ({ default: m.AccountSettingsPage })));
const CalendarPage = lazy(() => import("../pages/CalendarPage").then((m) => ({ default: m.CalendarPage })));
const DocumentationPage = lazy(() => import("../pages/DocumentationPage").then((m) => ({ default: m.DocumentationPage })));
const GoalsPage = lazy(() => import("../pages/GoalsPage").then((m) => ({ default: m.GoalsPage })));
const NotificationsPage = lazy(() => import("../pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));

const PlatformWorkspace = lazy(() => import("../pages/platform/PlatformWorkspace").then((m) => ({ default: m.PlatformWorkspace })));
const PlatformOverviewTab = lazy(() => import("../pages/platform/OverviewPage").then((m) => ({ default: m.OverviewPage })));
const BusinessesPage = lazy(() => import("../pages/platform/BusinessesPage").then((m) => ({ default: m.BusinessesPage })));
const BusinessDetailPage = lazy(() => import("../pages/platform/BusinessDetailPage").then((m) => ({ default: m.BusinessDetailPage })));
const UsersPage = lazy(() => import("../pages/platform/UsersPage").then((m) => ({ default: m.UsersPage })));
const UserDetailPage = lazy(() => import("../pages/platform/UserDetailPage").then((m) => ({ default: m.UserDetailPage })));
const SubscriptionsPage = lazy(() => import("../pages/platform/SubscriptionsPage").then((m) => ({ default: m.SubscriptionsPage })));
const RevenuePage = lazy(() => import("../pages/platform/RevenuePage").then((m) => ({ default: m.RevenuePage })));
const FunnelPage = lazy(() => import("../pages/platform/FunnelPage").then((m) => ({ default: m.FunnelPage })));
const LandingAnalyticsPage = lazy(() => import("../pages/platform/LandingAnalyticsPage").then((m) => ({ default: m.LandingAnalyticsPage })));
const RetentionPage = lazy(() => import("../pages/platform/RetentionPage").then((m) => ({ default: m.RetentionPage })));
const EngagementPage = lazy(() => import("../pages/platform/EngagementPage").then((m) => ({ default: m.EngagementPage })));
const ModulesPage = lazy(() => import("../pages/platform/ModulesPage").then((m) => ({ default: m.ModulesPage })));
const AlertsPage = lazy(() => import("../pages/platform/AlertsPage").then((m) => ({ default: m.AlertsPage })));
const HealthPage = lazy(() => import("../pages/platform/HealthPage").then((m) => ({ default: m.HealthPage })));
const SupportPage = lazy(() => import("../pages/platform/SupportPage").then((m) => ({ default: m.SupportPage })));
const AuditPage = lazy(() => import("../pages/platform/AuditPage").then((m) => ({ default: m.AuditPage })));

const TermsPage = lazy(() => import("../pages/TermsPage").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("../pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })));
const AcceptableUsePage = lazy(() => import("../pages/AcceptableUsePage").then((m) => ({ default: m.AcceptableUsePage })));
const DPAPage = lazy(() => import("../pages/DPAPage").then((m) => ({ default: m.DPAPage })));
const SubprocessorsPage = lazy(() => import("../pages/SubprocessorsPage").then((m) => ({ default: m.SubprocessorsPage })));
const CookiePolicyPage = lazy(() => import("../pages/CookiePolicyPage").then((m) => ({ default: m.CookiePolicyPage })));

// Legacy marketing URL — the landing page now lives at the root. Preserve the
// hash so old section links like /landing#pricing keep scrolling correctly.
function LandingRedirect() {
  const { hash } = useLocation();
  return <Navigate to={{ pathname: "/", hash }} replace />;
}

export function AppRouter() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Guest-only routes */}
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Public routes */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite" element={<JoinBusinessPage />} />
        <Route path="/landing" element={<LandingRedirect />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/acceptable-use" element={<AcceptableUsePage />} />
        <Route path="/dpa" element={<DPAPage />} />
        <Route path="/subprocessors" element={<SubprocessorsPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />

        {/* Root — landing page for guests, the app home for authenticated users.
            SetupGate renders the full-screen BusinessSetupPage (no AppLayout
            chrome) while the user has no business or is mid-setup. */}
        <Route element={<RootGate />}>
          <Route element={<SetupGate />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<HomePage />} />
            </Route>
          </Route>
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/suppliers" element={<FeatureGate feature="suppliers_enabled"><SuppliersPage /></FeatureGate>} />
            <Route path="/suppliers/:id" element={<FeatureGate feature="suppliers_enabled"><SupplierDetailPage /></FeatureGate>} />
            <Route path="/sales" element={<SalesPage />} />
            <Route path="/sales/:id" element={<SaleDetailPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/expenses/:id" element={<ExpenseDetailPage />} />
            <Route path="/catalog" element={<FeatureGate feature="catalog_enabled"><CatalogPage /></FeatureGate>} />
            <Route path="/catalog/:id" element={<FeatureGate feature="catalog_enabled"><CatalogDetailPage /></FeatureGate>} />
            <Route path="/inventory" element={<FeatureGate feature="inventory_enabled"><InventoryPage /></FeatureGate>} />
            <Route path="/inventory/:id" element={<FeatureGate feature="inventory_enabled"><InventoryDetailPage /></FeatureGate>} />
            <Route path="/employees" element={<FeatureGate feature="employees_enabled"><EmployeesPage /></FeatureGate>} />
            <Route path="/settings" element={<BusinessSettingsPage />} />
            <Route path="/account" element={<AccountSettingsPage />} />
            <Route path="/help" element={<DocumentationPage />} />
            <Route path="/calendar" element={<FeatureGate feature="calendar_enabled"><CalendarPage /></FeatureGate>} />
            <Route path="/goals" element={<GoalsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />

            {/* Platform admin — nested workspace with its own sub-nav */}
            <Route element={<PlatformAdminRoute />}>
              <Route path="/platform" element={<PlatformWorkspace />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<PlatformOverviewTab />} />
                <Route path="businesses" element={<BusinessesPage />} />
                <Route path="businesses/:id" element={<BusinessDetailPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/:id" element={<UserDetailPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="revenue" element={<RevenuePage />} />
                <Route path="funnel" element={<FunnelPage />} />
                <Route path="landing" element={<LandingAnalyticsPage />} />
                <Route path="retention" element={<RetentionPage />} />
                <Route path="engagement" element={<EngagementPage />} />
                <Route path="modules" element={<ModulesPage />} />
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="health" element={<HealthPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="audit" element={<AuditPage />} />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
